import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import {
  Print,
  Visibility,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";
import "react-toastify/dist/ReactToastify.css";
import { Chip } from '@mui/material';
import { generatePdf } from "../../Accounts/GeneralVoucherPrint";

const Expense = ({ data }) => {
  console.log("Expense Data:", data);
  
  // Check if data exists and has required structure
  const hasData = data[0] && data[0].headers && data[0].rows && data[0].headers.length > 0 && data[0].rows.length > 0;
  
  // Extract headers and rows from data
  const headers = hasData ? data[0].headers : [];
  const rows = hasData ? data[0].rows : [];
  // Separate expense headers and month total header
  const expenseHeaders = headers.filter(header => header.id !== "month_total");
  const monthTotalHeader = headers.find(header => header.id === "month_total");

  const formatAmount = (amount) => {
    if (amount === null || amount === undefined) return "0.00";
    const num = parseFloat(amount);
    return num === 0 ? "-" : num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const getExpenseAmount = (row, expenseId) => {
    if (!row.expense_totals || !row.expense_totals[expenseId]) return "0.00";
    const amount = row.expense_totals[expenseId];
    return amount === 0 || amount === "0.00" ? "-" : parseFloat(amount).toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const isGrandTotalRow = (row) => {
    return row.month_key === "grand_total";
  };

  return (
    <Box sx={{ width: '100%', overflow: 'auto' }}>
      <TableContainer
        component={Paper}
        sx={{
          maxHeight: '48vh',
          overflow: "auto",
          border: '1px solid #e0e0e0'
        }}
      >
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: Math.max(800, expenseHeaders.length * 150), // Dynamic width based on columns
            "& td, & th": {
              fontWeight: "400",
              borderRight: "1px solid #e0e0e0",
              textAlign: "center",
              whiteSpace: "nowrap",
              padding: "6px 8px",
              fontSize: "0.75rem"
            },
          }}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: "#f5f5f5",
                "& th": {
                  fontWeight: "bold !important",
                  color: "#333",
                  borderBottom: "2px solid #999",
                  whiteSpace: "nowrap",
                  pt: 0.5,
                  pb: 0.5,
                  borderRight: "1px solid #ccc"
                },
              }}
            >
              {/* Month Column */}
              <TableCell
                sx={{
                  position: "sticky",
                  left: 0,
                  backgroundColor: "#e3f2fd",
                  zIndex: 3,
                  borderRight: "2px solid #1976d2",
                  minWidth: 100
                }}
              >
                Month
              </TableCell>

              {/* Dynamic Expense Columns */}
              {expenseHeaders.map((header) => (
                <TableCell
                  key={header.id}
                  sx={{
                    minWidth: 150,
                    backgroundColor: "#f5f5f5",
                    fontWeight: "bold"
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight="bold" noWrap>
                      {header.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total: ₹{formatAmount(header.total)}
                    </Typography>
                  </Box>
                </TableCell>
              ))}

              {/* Month Total Column */}
              {monthTotalHeader && (
                <TableCell
                  sx={{
                    minWidth: 120,
                    backgroundColor: "#e8f5e9",
                    fontWeight: "bold",
                    borderLeft: "2px solid #4caf50"
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {monthTotalHeader.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Total: ₹{formatAmount(monthTotalHeader.total)}
                    </Typography>
                  </Box>
                </TableCell>
              )}

          
            </TableRow>
          </TableHead>

          <TableBody>
            {rows.length > 0 ? (
              rows.map((row, index) => {
                const isGrandTotal = isGrandTotalRow(row);
                
                return (
                  <TableRow
                    hover
                    key={row.month_key || index}
                    sx={{
                      backgroundColor: isGrandTotal ? "#e8f5e9" : (index % 2 === 0 ? "#fafafa" : "#ffffff"),
                      "&:hover": {
                        backgroundColor: isGrandTotal ? "#c8e6c9" : "#e3f2fd",
                      },
                      "& td": {
                        padding: "4px 8px",
                        fontSize: "0.75rem",
                        whiteSpace: "nowrap",
                        fontWeight: isGrandTotal ? "bold" : "normal",
                        borderRight: "1px solid #e0e0e0"
                      },
                    }}
                  >
                    {/* Month Cell */}
                    <TableCell
                      sx={{
                        position: "sticky",
                        left: 0,
                        backgroundColor: isGrandTotal ? "#c8e6c9" : (index % 2 === 0 ? "#f5f5f5" : "#ffffff"),
                        zIndex: 2,
                        borderRight: "2px solid #1976d2",
                        fontWeight: isGrandTotal ? "bold" : "medium"
                      }}
                    >
                      {row.month_display}
                    </TableCell>

                    {/* Dynamic Expense Amount Cells */}
                    {expenseHeaders.map((header) => {
                      const amount = getExpenseAmount(row, header.id);
                      const isZero = amount === "-";
                      
                      return (
                        <TableCell
                          key={`${row.month_key}-${header.id}`}
                          sx={{
                            color: isZero ? "#9e9e9e" : "inherit",
                            fontFamily: isZero ? "inherit" : "'Roboto Mono', monospace",
                            backgroundColor: isGrandTotal ? "#e8f5e9" : "inherit"
                          }}
                        >
                          {isZero ? "-" : `₹${amount}`}
                        </TableCell>
                      );
                    })}

                    {/* Month Total Cell */}
                    <TableCell
                      sx={{
                        backgroundColor: isGrandTotal ? "#c8e6c9" : "#f1f8e9",
                        borderLeft: "2px solid #4caf50",
                        fontFamily: "'Roboto Mono', monospace",
                        fontWeight: "bold",
                        color: "#1b5e20"
                      }}
                    >
                      ₹{formatAmount(row.month_total)}
                    </TableCell>

                  </TableRow>
                );
              })
            ) : (
              // Empty State
              <TableRow>
                <TableCell 
                  colSpan={(expenseHeaders.length + (monthTotalHeader ? 1 : 0) + 2)} 
                  align="center"
                  sx={{ py: 4 }}
                >
                  <Typography color="text.secondary">
                    {data && data.message ? data.message : "No expense data available"}
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Summary Info (Optional) */}
      {hasData && (
        <Box sx={{ 
          mt: 2, 
          display: 'flex', 
          gap: 2, 
          flexWrap: 'wrap',
          justifyContent: 'center' 
        }}>
          <Chip 
            label={`${expenseHeaders.length} Expenses`}
            size="small"
            color="primary"
            variant="outlined"
          />
          <Chip 
            label={`${rows.length - 1} Months`} // Subtract 1 for grand total row
            size="small"
            color="secondary"
            variant="outlined"
          />
          {monthTotalHeader && (
            <Chip 
              label={`₹${formatAmount(monthTotalHeader.total)} Total`}
              size="small"
              color="success"
              sx={{ fontWeight: 'bold' }}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default Expense;